/**
 * Tree.jsx - Interactive Talk Tree Visualization Component
 * 
 * Renders a dynamic tree visualization representing student talk moves and engagement
 * during tutoring or classroom sessions. Features include:
 * - Student talk move flowers (questions, arguments, explanations, etc.)
 * - Dynamic tree structure that grows with more talk moves
 * - Prototype tutor visualization components (root balls, watering can, rain cloud)
 * - Session metadata and tree fullness indicators
 * - Interactive selection and hover states for analytics
 * - The tree respects the `treeScale` prop passed down from SessionTrees' grid
 *   layout, so it shrinks to fit its column instead of overflowing the card at
 *   narrow widths. The overlay's tree pane renders at full native size
 *   (scale 1), since it isn't constrained by the grid column.
 * - Session metadata has ONE presentation: the overlay. Clicking the title row
 *   opens a modal holding the tree and its metadata side by side —
 *   metadata left, tree right on desktop; tree on top, metadata below as the
 *   screen narrows. The earlier inline Collapse and its caret are gone, so
 *   there is no second, competing way to read the same information.
 * - Colors come from the shared theme (COLORS) rather than literals.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './Tree.css';
import Flower from './components/Flower';
import MainBranch from './components/MainBranch';
import RootBallV2 from './components/RootBallV2';
import WateringCanV2 from './components/WateringCanV2';
import RainCloudV2 from './components/RainCloudV2';
import {
  BASE_FLOWERS,
  generateCompleteTreeStructure,
  getDynamicBranchStyles,
  calculateTreeDimensions,
  calculateRequiredBranches,
  typeFlowerColors,
  TREE_SCALE,
  DEV_MAX_BRANCH_COUNT,
  DEV_MAX_TRUNK_HEIGHT,
  hasDemoFeaturesParam
} from './constants/constants';
import { processTreeDataForCounts } from './components/treeDataProcessor';
import { getTalkMoveTypeFlowerThresholds, isActiveTMGroup } from './utils/treeUtils';
import { SessionTimeInfo } from './TimezoneUtility';
import { generateFlowerKeyAltText, generateTreeAltText } from '../Tree/components/treeAltText';
import {
  Tooltip, IconButton, Typography, Box, ToggleButtonGroup, ToggleButton,
  Dialog, DialogTitle, DialogContent, useMediaQuery,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { UNTRANSCRIBED_THRESHOLD } from './constants/constants';
import { appTheme, COLORS, FONT_FAMILY } from './constants/theme';

const RADII = { panel: 16, card: 14, control: 8 };

// Fixed width for the student/tutor talk-move tables beside each tree. The
// tables render at whatever width their content needs (e.g. "Keeping
// Together" vs "Relating"), and since each tree row is centered as a whole
// [label + tree + table] block, unequal table widths shift the two trees'
// trunks out of alignment even when the tree boxes themselves match. Pinning
// both tables to the same width removes that variable.
const SIDE_PANEL_WIDTH = 190;

export function Tree({
  containerRef,
  processTranscriptData,
  sessionId,
  yearInputType,
  region,
  retrieveSagaUserRegion,
  isStudentEngagementPage,
  // Shows the overlay's Students/Math Topics/Session Overview fields without
  // also pulling in isStudentEngagementPage's other effects (tree-select
  // click behavior, the library's legend row) for pages that just want the
  // richer session info.
  showSessionDetails = false,
  client,
  isSelected,
  onTreeSelect,
  showTutorVisualization = false,
  tutorVisualizationType = 'tree',
  maxTreeHeight,
  showDescriptionOnly = false,
  showSpeakerLabels = false,
  studentSidePanel = null,
  tutorSidePanel = null,
  // Uniform shrink factor computed by SessionTrees from the measured grid
  // column width. 1 = native size. Applied to the card's tree box, its
  // watermark, and the title pill's max-width so the tree never exceeds the
  treeScale = 1,
}) {
  const [sessionMetadata, setSessionMetadata] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [tutorId, setTutorId] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [treeFullnessInfo, setTreeFullnessInfo] = useState(null);
  const [visualizationType, setVisualizationType] = useState(tutorVisualizationType);
  const [descriptionOpen, setDescriptionOpen] = useState(false);

  const [overlayOpen, setOverlayOpen] = useState(false);
  const muiTheme = useTheme();
  const isWideScreen = useMediaQuery(muiTheme.breakpoints.up('md'));
  const showDemoFeatures = hasDemoFeaturesParam();

  const handleOpenOverlay = (e) => {
    if (e) e.stopPropagation();
    setOverlayOpen(true);
  };
  const handleCloseOverlay = () => setOverlayOpen(false);

  const handleVisualizationTypeChange = (event, newType) => {
    if (newType !== null) {
      setVisualizationType(newType);
    }
  };

  const [treeData, setTreeData] = useState({
    student: {
      statements: [],
      processedData: {},
      flowerAllocation: {},
      chronologicalFlowers: [],
      totalFlowers: 0,
      totalLeaves: 0,
      treeFullnessInfo: null
    },
    tutor: {
      statements: [],
      processedData: {},
      flowerAllocation: {},
      chronologicalFlowers: [],
      totalFlowers: 0,
      totalLeaves: 0,
      treeFullnessInfo: null
    }
  });

  const handleTreeVisualizationKeyDown = (e) => {
    if (isStudentEngagementPage && onTreeSelect) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        onTreeSelect(sessionId);
      }
    }
  };

  const handleTreeVisualizationClick = (e) => {
    if (isStudentEngagementPage && onTreeSelect) {
      e.stopPropagation();
      onTreeSelect(sessionId);
    }
  };

  const calculateUntranscribedPercentage = (statements) => {
    if (!statements || statements.length === 0) {
      return 0;
    }
    
    const untranscribedCount = statements.filter(
      statement => statement.text === "[Untranscribed]"
    ).length;
    
    const total = statements.length;
    return (untranscribedCount / total) * 100;
  };

  const hasExcessiveUntranscribed = (statements) => {
    const untranscribedPercentage = calculateUntranscribedPercentage(statements);
    return untranscribedPercentage > UNTRANSCRIBED_THRESHOLD;
  };

  const InsufficientTranscriptionMessage = () => (
    <div className="no-data-message" role="status" aria-live="polite" style={{
      height: '350px',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
    }}>
      <p style={{ textAlign: 'left', paddingLeft: '10px', paddingRight: '10px' }}>Not enough student talk was transcribed to reliably generate a tree.</p>
    </div>
  );

  const formatTime = (timeInSeconds) => {
    if (timeInSeconds == null || isNaN(timeInSeconds)) return '00:00';
    const minutes = Math.round(timeInSeconds / 60);
    return `${minutes}`;
  };

  const isSampleSession = (title) => {
    const lower = title?.toLowerCase() ?? '';
    return lower.startsWith('sample session') || lower.startsWith('sample tree');
  };

  useEffect(() => {
    if (sessionId) {
      setCurrentSessionId(sessionId);
      setTutorId(null);
      setIsLoaded(false);
      setSessionMetadata(null);
      setOverlayOpen(false);
    }
  }, [sessionId]);

  const processTreeData = useCallback(async (speakerType) => {
    if (!currentSessionId) {
      return {
        statements: [],
        processedData: {},
        flowerAllocation: {},
        sessionMetadata: null,
        chronologicalFlowers: [],
        totalFlowers: 0,
        totalLeaves: 0,
        treeFullnessInfo: null
      };
    }

    const inputType = yearInputType;

    try {
      const response = await processTranscriptData(currentSessionId, inputType);

      if (!response) return null;

      const { processedData: data, sessionMetadata } = response;

      if (data && Array.isArray(data)) {
        const filteredStatements = data.filter(item =>
          item.speakerType.toLowerCase() === speakerType.toLowerCase()
        );

        const calculatedData = processTreeDataForCounts(data, speakerType, sessionMetadata);

        return {
          statements: filteredStatements,
          processedData: calculatedData.processedDataCounts,
          flowerAllocation: calculatedData.flowerAllocation,
          chronologicalFlowers: calculatedData.chronologicalFlowers,
          sessionMetadata,
          totalFlowers: calculatedData.totalFlowers,
          totalLeaves: calculatedData.totalLeaves,
          treeFullnessInfo: calculatedData.treeFullnessInfo
        };
      } else {
        return {
          statements: [],
          processedData: {},
          flowerAllocation: {},
          sessionMetadata: null,
          chronologicalFlowers: [],
          totalFlowers: 0,
          totalLeaves: 0,
          treeFullnessInfo: null
        };
      }
    } catch (error) {
      return {
        statements: [],
        processedData: {},
        flowerAllocation: {},
        sessionMetadata: null,
        chronologicalFlowers: [],
        totalFlowers: 0,
        totalLeaves: 0,
        treeFullnessInfo: null
      };
    }
  }, [processTranscriptData, currentSessionId, yearInputType]);

  useEffect(() => {
    if (sessionMetadata?.participants) {
      const tutor = sessionMetadata.participants.find(p => p.type === 'tutor');
      if (tutor?.id) {
        setTutorId(tutor.id);
      }
    }
  }, [sessionMetadata]);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentSessionId) return;

    // Fetch both student and tutor data
      const studentTreeData = await processTreeData('student', sessionMetadata);
      const tutorTreeData = await processTreeData('tutor', sessionMetadata);

      if (studentTreeData) {
        setTreeData(prevData => ({
          ...prevData,
          student: {
            ...studentTreeData,
            treeFullnessInfo: studentTreeData.treeFullnessInfo
          },
          tutor: tutorTreeData || prevData.tutor
        }));

        const metadata = studentTreeData.sessionMetadata;
        setSessionMetadata(metadata);

        if (studentTreeData.treeFullnessInfo) {
          setTreeFullnessInfo(studentTreeData.treeFullnessInfo);
        }

        const tutor = metadata?.participants?.find(p => p.type === 'tutor');
        if (tutor?.id) {
          setTutorId(tutor.id);
        }

        setIsLoaded(true);
      }
    };

    fetchData();
  }, [currentSessionId, processTreeData]);
  const getUtterancesForFlower = useCallback((flowerId, flowerType, studentData) => {
    const { chronologicalFlowers, statements } = studentData;
    if (!chronologicalFlowers || !statements) return [];

    const flowersOfSameType = chronologicalFlowers.filter(f => f.type === flowerType);
    const flowerIndex = flowersOfSameType.findIndex(f => f.id === flowerId);
    if (flowerIndex === -1) return [];

    const typeStatements = statements.filter(
      s => isActiveTMGroup(s.talkMoveType, 'type') && s.talkMoveType === flowerType
    );

    const thresholds = getTalkMoveTypeFlowerThresholds('student');
    const threshold = thresholds[flowerType];
    if (!threshold) return [];

    const startIndex = flowerIndex * threshold;
    const endIndex = Math.min((flowerIndex + 1) * threshold, typeStatements.length);
    if (startIndex >= typeStatements.length) return [];

    const rangeStatements = typeStatements.slice(startIndex, endIndex);

    // Sort longest-first so the clearest example leads, but keep every one.
    return rangeStatements
      .map(s => s.text)
      .filter(Boolean)
      .sort((a, b) => b.length - a.length);
  }, []);

  const flowerUtterances = useMemo(() => {
    if (!showDemoFeatures || !isSelected || !isLoaded) return {};
    const utterances = {};
    (treeData.student.chronologicalFlowers || []).forEach(flower => {
      const list = getUtterancesForFlower(flower.id, flower.type, treeData.student);
      if (list.length > 0) utterances[flower.id] = list;
    });
    return utterances;
  }, [showDemoFeatures, isSelected, isLoaded, treeData.student, getUtterancesForFlower]);

  const getFlowerUtterance = useCallback((flowerId) => flowerUtterances[flowerId], [flowerUtterances]);

  const branchCountOverride = showDemoFeatures ? DEV_MAX_BRANCH_COUNT : undefined;
  const trunkHeightOverride = showDemoFeatures ? DEV_MAX_TRUNK_HEIGHT : undefined;

  const createTreeRenderHelpers = (treeData, speakerType) => {
    const getFlowerColor = (flowerId) => {
      const flowerNum = parseInt(flowerId.replace('f', ''));
      const flower = treeData.chronologicalFlowers?.[flowerNum - 1];

      if (!flower) return 'whiteflower';
      return typeFlowerColors[flower.type] || 'whiteflower';
    };

    const shouldShowBranch = (branchId) => {
      return true;
    };

    const shouldShowLeaf = (leafNum) => {
      const totalLeaves = treeData.totalLeaves || 0;
      return leafNum <= totalLeaves;
    };

    const shouldShowFlower = (flowerId) => {
      const flowerNum = parseInt(flowerId.replace('f', ''));
      const totalFlowers = treeData.totalFlowers || 0;
      return flowerNum <= totalFlowers;
    };

    return {
      getFlowerColor,
      shouldShowBranch,
      shouldShowLeaf,
      shouldShowFlower,
      processedData: treeData.processedData,
      statements: treeData.statements
    };
  };

  const studentTreeHelpers = createTreeRenderHelpers(treeData.student, 'student');
  const tutorTreeHelpers = createTreeRenderHelpers(treeData.tutor, 'tutor');

  const tutorTotalFlowers = treeData.tutor.totalFlowers || 0;
  const tutorTotalLeaves = treeData.tutor.totalLeaves || 0;
  const tutorBranchInfo = calculateRequiredBranches(
    tutorTotalLeaves,
    tutorTotalFlowers,
    branchCountOverride
  );
  const tutorTreeStructureData = generateCompleteTreeStructure(tutorTotalFlowers, tutorTotalLeaves, tutorBranchInfo.branchCount);
  const tutorDimensions = calculateTreeDimensions(tutorBranchInfo.branchCount, trunkHeightOverride);

  const NoDataMessage = () => (
    <div className="no-data-message" role="status" aria-live="polite">
      <p>No student talk was transcribed.</p>
    </div>
  );

  if (!isLoaded) {
    return (
      <div className="loading-container" role="status" aria-live="polite" aria-busy="true">
        <p>Loading tree...</p>
      </div>
    );
  }

  const renderTreeFullnessInfo = (completeTreeStructureData) => {
    const treeFullnessInfo = treeData.student.treeFullnessInfo;

    if (!treeFullnessInfo) {
      return null;
    }
    const totalFlowers = treeData.student.totalFlowers || 0;

    const tooltipText = `A full talk tree indicates that students made ${totalFlowers} talk moves during the session. This tree reached its maximum capacity at ${formatTime(treeFullnessInfo.elapsedTime)} minutes out of the total ${formatTime(treeFullnessInfo.totalSessionTime)} minute session. This shows strong student engagement and discourse throughout the lesson.`;

    return (
      <ThemeProvider theme={appTheme}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }} role='status'>
        <strong style={{ fontSize: '0.875rem', fontWeight: 600 }}>
          The talk tree filled at {formatTime(treeFullnessInfo.elapsedTime)} of {formatTime(treeFullnessInfo.totalSessionTime)} minutes.
        </strong>
        <Tooltip
          title={tooltipText}
          arrow
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: COLORS.textMuted,
                color: COLORS.page,
                fontSize: '0.875rem',
                maxWidth: '25rem',
                padding: '0.75rem',
                '& .MuiTooltip-arrow': {
                  color: COLORS.textMuted,
                },
              },
            },
          }}
        >
          <IconButton
            size="small"
            aria-label="More information about tree fullness"
            sx={{
              padding: '0.125rem',
              color: COLORS.textMuted,
              '&:hover': {
                backgroundColor: COLORS.hairline,
              }
            }}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
      </ThemeProvider>
    );
  };

  const hasStudentData = treeData.student.statements.length > 0;
  const hasTutorData = treeData.tutor.statements.length > 0;

  const totalFlowers = treeData.student.totalFlowers || 0;
  const totalLeaves = treeData.student.totalLeaves || 0;

  // Calculate required branches based on student talk
  const branchInfo = calculateRequiredBranches(
    totalLeaves,
    totalFlowers,
    branchCountOverride
  );
  const { branchCount } = branchInfo;

  // Calculate tree dimensions based on branch count
  const dimensions = calculateTreeDimensions(branchCount, trunkHeightOverride);

  // Generate tree structure
  const completeTreeStructureData = generateCompleteTreeStructure(totalFlowers, totalLeaves, branchCount);
  const estimatedTreeWidth = Math.min(450, 350 + (completeTreeStructureData.branches.length * 10));
  const cardWidth = estimatedTreeWidth * TREE_SCALE;

  const buildTreeVisualization = (scale = 1) => (
    <div
      className="tree-visualization"
      onClick={handleTreeVisualizationClick}
      onKeyDown={handleTreeVisualizationKeyDown}
      tabIndex={isStudentEngagementPage ? 0 : -1}
      role={isStudentEngagementPage ? "button" : undefined}
      aria-label={isStudentEngagementPage ? `Select tree for ${sessionMetadata?.title}` : undefined}
      style={{
        position: 'relative',
        height: `${maxTreeHeight * scale}px`,
        width: `${cardWidth * scale}px`,
        margin: (showTutorVisualization && hasTutorData && visualizationType === 'tree') ? '0' : '0 auto',
        cursor: isStudentEngagementPage ? 'pointer' : 'default',
        padding: (showTutorVisualization && hasTutorData && visualizationType === 'tree') ? '0px' : '5px',
        backgroundColor: 'transparent',
        transition: 'all 0.2s ease-in-out',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
    >
      {/* Sample Session Watermark */}
      {isSampleSession(sessionMetadata?.title) && (
        <div
          style={{
            position: 'absolute',
            top: `${-20 * scale}px`,
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: `${40 * scale}px`,
            fontWeight: 'bold',
            color: 'rgba(226, 223, 223, 0.4)',
            textTransform: 'uppercase',
            letterSpacing: `${8 * scale}px`,
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 0,
            textShadow: '0 0 2px rgba(173, 168, 168, 0.5)',
          }}
          aria-hidden="true"
        >
          SAMPLE
        </div>
      )}

      <div
        className="tree-scale-wrapper"
        style={{
          transform: `scale(${TREE_SCALE * scale})`,
          transformOrigin: 'bottom center',
        }}
      >
        {hasStudentData ? (
          <div className="tree-display" data-session-id={sessionId} role='img'
            aria-label={generateTreeAltText(treeData.student, 'student')}
          >
            {(() => {
              const completeTreeStructure = completeTreeStructureData.branches;

              return (
                <div ref={containerRef} className="background" style={{ height: `${dimensions.backgroundHeight}px`, width: '350px', overflow: 'visible' }}>
                  <div className="slope" aria-hidden="true"></div>
                  <div className="tree" aria-hidden="true" style={{ '--trunk-height': `${dimensions.trunkHeight}px` }}>

                      {/* First 3 leaves on trunk */}
                    {[1, 2, 3].map(leafNum => (
                      studentTreeHelpers.shouldShowLeaf(leafNum) && (
                        <div
                          key={`student-leaf-${leafNum}`}
                          id={`student-leaf-${leafNum}`}
                          style={{ display: 'block' }}
                          className={`leaf leaf${leafNum}`}
                          aria-hidden="true"
                        />
                      )
                    ))}

                      {/* First flower on trunk */}
                    {studentTreeHelpers.shouldShowFlower('f1') && (
                      <Flower
                        flowerId={'f1'}
                        color={studentTreeHelpers.getFlowerColor('f1')}
                        utterance={getFlowerUtterance('f1')}
                      />
                    )}

                      {/* All branches */}
                    {completeTreeStructure.map((branch) => {
                      const dynamicStyles = branch.isDynamic
                        ? getDynamicBranchStyles(branch.branchNum, dimensions.trunkHeight)
                        : undefined;

                      return (
                        <MainBranch
                          key={`student-${branch.id}`}
                          {...branch}
                          shouldShowLeaf={studentTreeHelpers.shouldShowLeaf}
                          shouldShowFlower={studentTreeHelpers.shouldShowFlower}
                          shouldShowBranch={studentTreeHelpers.shouldShowBranch}
                          getFlowerColor={studentTreeHelpers.getFlowerColor}
                          getFlowerUtterance={getFlowerUtterance}
                          style={dynamicStyles}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          <NoDataMessage />
        )}
      </div>
    </div>
  );

  const sessionInfoDetails = (
    <>
      {/* Students */}
      {client !== "TeachFX" && (isStudentEngagementPage || showSessionDetails) && (
        <Typography paragraph sx={{ marginBottom: '0.75rem',    textAlign: 'left', fontSize: '0.875rem' }}>
          <strong style={{ fontWeight: 600 }}>Students:</strong>{" "}
          {(() => {
            const names = (sessionMetadata?.activeParticipants ?? [])
              .map((participant) => (client === "Demo" || client === "TeachFX" ? participant.id : participant.name) ?? "Unknown");
            const allUnknown = names.length > 0 && names.every((name) => name === "Unknown");
            return allUnknown ? String(names.length) : names.sort((a, b) => a.localeCompare(b)).join(", ");
          })()}
        </Typography>
      )}

        {/* TeachFX specific info */}
      {client === "TeachFX" && sessionMetadata.grade && sessionMetadata.subject && (
        <Box sx={{ marginBottom: '0.75rem', textAlign: 'left' }}>
          <Typography paragraph sx={{ marginBottom: '0.5rem',    textAlign: 'left', fontSize: '0.875rem' }}>
            <strong>Subject:</strong> {sessionMetadata.subject}
          </Typography>
          <Typography paragraph sx={{ marginBottom: '0.5rem',    textAlign: 'left', fontSize: '0.875rem' }}>
            <strong>Grade:</strong> {sessionMetadata.grade}
          </Typography>
        </Box>
      )}

        {/* Session Time Info */}
      <Box sx={{ marginBottom: '0.75rem',    textAlign: 'left', fontSize: '0.875rem' }}>
        <SessionTimeInfo
          sessionMetadata={sessionMetadata}
          region={region}
          retrieveSagaUserRegion={retrieveSagaUserRegion}
          client={client}
          isStudentEngagementPage={isStudentEngagementPage}
        />
      </Box>

        {/* Lesson Summary */}
      {(client === "TeachFX") && sessionMetadata.lessonSummary && (
        <Box sx={{ marginBottom: '0.75rem', textAlign: 'left' }}>
          <Typography paragraph sx={{ marginBottom: '0.25rem',    textAlign: 'left', fontSize: '0.875rem' }}>
            <strong>Lesson Summary:</strong>
          </Typography>
          <Box sx={{ marginTop: '0.5rem', paddingLeft: '0.625rem',    textAlign: 'left', fontSize: '0.875rem' }}>
            {sessionMetadata.lessonSummary}
          </Box>
        </Box>
      )}

        {/* Math Topics */}
      {hasStudentData && (isStudentEngagementPage || showSessionDetails) && sessionMetadata?.feedbackMathTopics && (
        <Box sx={{ marginBottom: '0.75rem', textAlign: 'left' }}>
          <Typography paragraph sx={{ marginBottom: '0.5rem',    textAlign: 'left', fontSize: '0.875rem' }}>
            <strong>Math Topics:</strong>
          </Typography>
          <Box sx={{ paddingLeft: '0.625rem',    textAlign: 'left', fontSize: '0.875rem' }}>
            <style>
              {`
                .math-topics-content ul {
                  padding-left: 2em;
                  text-align: left;
                  fontSize: 0.875rem
                }
                .math-topics-content li {
                  margin-left: 0;
                  text-align: left;
                  fontSize: 0.875rem
                }
                .math-topics-content {
                  text-align: left;
                  fontSize: 0.875rem
                }
              `}
            </style>
            <div
              className="math-topics-content"
              dangerouslySetInnerHTML={{
                __html: sessionMetadata.feedbackMathTopics.replace(
                  /Math Topics( Covered)?:\s*/i,
                  ''
                )
              }}
            />
          </Box>
        </Box>
      )}

        {/* Session Overview */}
      {hasStudentData && (isStudentEngagementPage || showSessionDetails) && sessionMetadata?.feedbackSummary && (
        <Box sx={{ marginBottom: '0.75rem', textAlign: 'left' }}>
          <Typography paragraph sx={{ marginBottom: '0.5rem',    textAlign: 'left', fontSize: '0.875rem' }}>
            <strong>Session Overview:</strong>
          </Typography>
          <Box sx={{ paddingLeft: '0.625rem',    textAlign: 'left', fontSize: '0.875rem' }}>
            <style>
              {`
                .session-overview-content ul {
                  padding-left: 2em;
                  text-align: left;
                  fontSize: 0.875rem
                }
                .session-overview-content li {
                  margin-left: 0;
                  text-align: left;
                  fontSize: 0.875rem
                }
                .session-overview-content {
                  text-align: left;
                  fontSize: 0.875rem
                }
              `}
            </style>
            <div
              className="session-overview-content"
              dangerouslySetInnerHTML={{
                __html: sessionMetadata.feedbackSummary.replace(
                  /Session Overview:\s*/i,
                  '<ul>'
                )
              }}
            />
          </Box>
        </Box>
      )}

        {/* Untranscribed Percentage for TeachFX */}
      {client === "TeachFX" && hasStudentData && (
        <Typography paragraph sx={{ marginBottom: '0.75rem',    textAlign: 'left', fontSize: '0.875rem' }}>
          <strong style={{fontWeight: 600}}>Untranscribed:</strong>{" "}
          {calculateUntranscribedPercentage(treeData.student.statements).toFixed(0)}%
        </Typography>
      )}

        {/* Tree Fullness Info */}
      {hasStudentData && renderTreeFullnessInfo(completeTreeStructureData)}
    </>
  );

  return (
    <ThemeProvider theme={appTheme}>
    <div
      className="tree-container"
      aria-label="Student talk tree visualization"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: '100%',
        marginBottom: 'auto',
      }}
    >

      {/* Tutor Visualization Toggle - only show if tutor viz is enabled */}
      {showTutorVisualization && hasTutorData && (
        <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
          <ToggleButtonGroup
            value={visualizationType}
            exclusive
            onChange={handleVisualizationTypeChange}
            aria-label="tutor visualization type"
            size="small"
          >
            <ToggleButton value="tree" aria-label="tutor talk tree visualization">
              Talk Tree
            </ToggleButton>
            <ToggleButton value="rootball" aria-label="root ball visualization">
              Root Ball
            </ToggleButton>
            <ToggleButton value="wateringcan" aria-label="watering can visualization">
              Watering Can
            </ToggleButton>
            <ToggleButton value="raincloud" aria-label="rain cloud visualization">
              Rain Cloud
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

        {/* Tutor Visualization (WateringCan / RainCloud) - above student tree */}
      {showTutorVisualization && hasTutorData && (visualizationType === 'wateringcan' || visualizationType === 'raincloud') && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {showSpeakerLabels && (
            <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontWeight: 600, fontSize: '0.8rem', color: COLORS.textMuted, whiteSpace: 'nowrap', padding: '0 4px', userSelect: 'none' }}>
              Tutor
            </span>
          )}
          <div style={{ flex: 1 }}>
            {visualizationType === 'wateringcan' && <WateringCanV2 tutorData={treeData.tutor} />}
            {visualizationType === 'raincloud' && <RainCloudV2 tutorData={treeData.tutor} />}
          </div>
          {tutorSidePanel}
        </div>
      )}

        {/* Student Tree Section */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {showSpeakerLabels && showTutorVisualization && hasTutorData && (
          <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontWeight: 600, fontSize: '0.8rem', color: COLORS.textMuted, whiteSpace: 'nowrap', padding: '0 4px', userSelect: 'none' }}>
            Student
          </span>
        )}
      {buildTreeVisualization(treeScale)}
      {studentSidePanel && (
        <div style={{ width: `${SIDE_PANEL_WIDTH}px`, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
          {studentSidePanel}
        </div>
      )}
      </div>

        {/* Tutor Talk Tree - BELOW THE STUDENT TREE, INVERTED */}
      {showTutorVisualization && hasTutorData && visualizationType === 'tree' && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {showSpeakerLabels && (
            <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontWeight: 600, fontSize: '0.8rem', color: COLORS.textMuted, whiteSpace: 'nowrap', padding: '0 4px', userSelect: 'none' }}>
              Tutor
            </span>
          )}
          <div style={{ transform: 'scaleY(-1)' }}>
          <div style={{
            position: 'relative',
            height: `${tutorDimensions.backgroundHeight * TREE_SCALE * treeScale}px`,
            // Match the student tree's container width (not the tutor's own
            // branch-derived width) so the trunk lines up with the tree above,
            // regardless of how many branches the tutor tree needs.
            width: `${cardWidth * treeScale}px`,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}>
            <div className="tree-scale-wrapper" style={{ transform: `scale(${TREE_SCALE * treeScale})`, transformOrigin: 'bottom center' }}>
              <div className="tree-display" role="img" aria-label={generateTreeAltText(treeData.tutor, 'tutor')}>
                <div className="background" style={{ height: `${tutorDimensions.backgroundHeight}px`, width: '350px', overflow: 'visible' }}>
                  <div className="slope" aria-hidden="true"></div>
                  <div className="tree" aria-hidden="true" style={{ '--trunk-height': `${tutorDimensions.trunkHeight}px` }}>
                    {[1, 2, 3].map(leafNum => (
                      tutorTreeHelpers.shouldShowLeaf(leafNum) && (
                        <div
                          key={`tutor-leaf-${leafNum}`}
                          id={`tutor-leaf-${leafNum}`}
                          style={{ display: 'block' }}
                          className={`leaf leaf${leafNum}`}
                          aria-hidden="true"
                        />
                      )
                    ))}
                    {tutorTreeHelpers.shouldShowFlower('f1') && (
                      <Flower flowerId={'f1'} color={tutorTreeHelpers.getFlowerColor('f1')} />
                    )}
                    {tutorTreeStructureData.branches.map((branch) => {
                      const dynamicStyles = branch.isDynamic
                        ? getDynamicBranchStyles(branch.branchNum, tutorDimensions.trunkHeight)
                        : undefined;
                      return (
                        <MainBranch
                          key={`tutor-${branch.id}`}
                          {...branch}
                          shouldShowLeaf={tutorTreeHelpers.shouldShowLeaf}
                          shouldShowFlower={tutorTreeHelpers.shouldShowFlower}
                          shouldShowBranch={tutorTreeHelpers.shouldShowBranch}
                          getFlowerColor={tutorTreeHelpers.getFlowerColor}
                          style={dynamicStyles}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
          {tutorSidePanel && (
            <div style={{ width: `${SIDE_PANEL_WIDTH}px`, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
              {tutorSidePanel}
            </div>
          )}
        </div>
      )}

        {/* Root Ball Visualization - BELOW THE TREE */}
      {showTutorVisualization && hasTutorData && visualizationType === 'rootball' && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {showSpeakerLabels && (
            <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontWeight: 600, fontSize: '0.8rem', color: COLORS.textMuted, whiteSpace: 'nowrap', padding: '0 4px', userSelect: 'none' }}>
              Tutor
            </span>
          )}
          <div style={{ flex: 1 }}>
            <RootBallV2 tutorData={treeData.tutor} />
          </div>
          {tutorSidePanel}
        </div>
      )}

      {sessionMetadata && (showDescriptionOnly ? (
        <Tooltip
          title={sessionMetadata.description || ''}
          arrow
          placement="top"
          open={descriptionOpen}
          disableHoverListener
          disableFocusListener
          disableTouchListener
          onClose={() => setDescriptionOpen(false)}
          PopperProps={{
            modifiers: [
              { name: 'offset', options: { offset: [0, 8] } },
              { name: 'preventOverflow', options: { boundary: 'viewport' } },
            ],
          }}
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: COLORS.primary,
                color: COLORS.page,
                fontSize: '0.85rem',
                maxWidth: `${cardWidth * treeScale}px`,
                padding: '0.75rem 1rem',
                borderRadius: `${RADII.control}px`,
                lineHeight: 1.6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                '& .MuiTooltip-arrow': { color: COLORS.primary },
              },
            },
          }}
        >
          <Box
            onClick={() => sessionMetadata.description && setDescriptionOpen(prev => !prev)}
            sx={{
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              margin: '0.5rem auto',
              border: `1px solid ${COLORS.border}`,
              borderRadius: `${RADII.card}px`,
              padding: '0.5rem 0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              backgroundColor: COLORS.page,
              cursor: sessionMetadata.description ? 'pointer' : 'default',
            }}
          >
            <Typography
              component="h4"
              sx={{
                fontFamily: `${FONT_FAMILY} !important`,
                fontWeight: 400,
                fontSize: '0.9rem',
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {sessionMetadata.title}
            </Typography>

            {sessionMetadata.description && (
              <InfoOutlinedIcon
                fontSize="small"
                sx={{
                  color: COLORS.textMuted,
                  flexShrink: 0,
                  fontSize: '1.1rem',
                }}
              />
            )}
          </Box>
        </Tooltip>

      ) : (
        <>
        <Box
          onClick={handleOpenOverlay}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpenOverlay(e); } }}
          role="button"
          tabIndex={0}
          aria-haspopup="dialog"
          aria-label={`View session information for ${sessionMetadata.title}`}
          sx={{
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            margin: '0.5rem auto',
            boxShadow: 'none',
            border: `1px solid ${COLORS.border}`,
            borderRadius: `${RADII.card}px`,
            backgroundColor: COLORS.page,
            padding: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.375rem',
            cursor: 'pointer',
            transition: 'border-color .15s ease',
            '&:hover': { borderColor: COLORS.primary },
            '&:focus-visible': { outline: `3px solid ${COLORS.primary}`, outlineOffset: '1px' },
          }}
        >
          <Typography
            component="h4"
            sx={{
              fontFamily: `${FONT_FAMILY} !important`,
              fontWeight: 400,
              fontSize: '0.9rem',
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {sessionMetadata.title}
          </Typography>

          <InfoOutlinedIcon
            aria-hidden="true"
            sx={{ fontSize: '1.1rem', color: COLORS.textMuted, flexShrink: 0 }}
          />
        </Box>

        <Dialog
          open={overlayOpen}
          onClose={handleCloseOverlay}
          maxWidth={isWideScreen ? 'md' : 'sm'}
          fullWidth={!isWideScreen}
          aria-labelledby={`tree-overlay-title-${sessionId}`}
          slotProps={{ paper: { sx: { borderRadius: `${RADII.panel}px` } } }}
        >
          <DialogTitle
            id={`tree-overlay-title-${sessionId}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pr: 5,
              py: 1.25,
              fontFamily: FONT_FAMILY,
              fontSize: '1rem',
            }}
          >
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {sessionMetadata.title}
            </span>
            <IconButton
              size="small"
              onClick={handleCloseOverlay}
              aria-label="Close"
              sx={{ position: 'absolute', right: 8, top: 8, color: COLORS.textMuted }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent
            dividers
            sx={{
              display: 'grid',
              gridTemplateColumns: isWideScreen ? 'auto minmax(0, 1fr)' : '1fr',
              alignItems: 'start',
              columnGap: 2.5,
              rowGap: 1.5,
              px: 2,
              py: 1.5,
            }}
          >
            <Box
              sx={{
                width: 'max-content',
                maxWidth: '100%',
                justifySelf: 'center',
                overflowX: 'auto',
              }}
            >
              {buildTreeVisualization(1)}
            </Box>

            <Box
              sx={{
                minWidth: 0,
                maxHeight: isWideScreen ? '70vh' : 'none',
                overflowY: isWideScreen ? 'auto' : 'visible',
                '& > :last-child': { marginBottom: 0 },
              }}
            >
              {sessionInfoDetails}
            </Box>
          </DialogContent>
        </Dialog>
        </>
      )
      )}
    </div>
    </ThemeProvider>
  );
}

export default Tree;